// Enhanced RestaurantOrderSummary with MUI styling
import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import {
  IconButton,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Grid,
  Paper,
  Box,
  Badge,
} from "@mui/material";
import {
  AddCircle,
  Edit,
  Search,
  RestartAlt,
  DeleteOutline,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useSelector } from "react-redux";

import CartModal from "./cartModal";
import { fAlertToast } from "../../../Utility/Utilitys";

const RestaurantOrderSummary = () => {
  const [filters, setFilters] = useState({
    fromDate: moment().subtract(8, "hours"),
    toDate: moment(),
    location: "",
    statusFilters: [],
    table: "",
  });

  const [cartMode, setCartMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [orderSummary, setOrderSummary] = useState([]);
  const [tableList, setTableList] = useState([]);

  const location = useSelector(
    (state) => state.SliceDB.value.Location.LocWithRights
  );
  const { UserID: User } = useSelector((state) => state.login.value);

  useEffect(() => {
    if (location.length > 0 && User) {
      const updatedFilters = {
        ...filters,
        location: location[0]?.Uid,
        User,
      };
      setFilters(updatedFilters);
      getOrderSummary(updatedFilters); // pass updated filters explicitly
      fetchTableList(location[0]?.Uid); // Load tables here
    }
  }, [location, User, filters.fromDate, filters.toDate]);

  const getOrderSummary = async (updatedFilters) => {
    // updated function signature to accept filters
    try {
      const rawData = await fetch(
        `http://178.23.187.46:3038/QuickSale/INV06TOA_Summary`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            StartDate: moment(updatedFilters.fromDate).format("MM-DD-YYYY"), // use updated filters
            EndDate: moment(updatedFilters.toDate).format("MM-DD-YYYY"), // use updated filters
            Loc: updatedFilters.location, // use updated filters
            User: updatedFilters.User, // use updated filters
          }),
        }
      );
      if (rawData.ok) {
        const res = await rawData.json();
        setOrderSummary(res.data);
      }
    } catch (error) {
      console.error("Error fetching order summary:", error);
    }
  };

  const fetchTableList = async (loc) => {
    try {
      const res = await fetch(
        "http://178.23.187.46:3038/QuickSale/INV06TO_Table_attributet",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Loc: loc }),
        }
      );
      const data = await res.json();
      if (data.Type === "Success") {
        setTableList(data.ResultSet || []);
        setFilters((prev) => ({
          ...prev,
          table: data.ResultSet[0]?.Uid || "", // Set default table if available
        }));
      }
    } catch (err) {
      console.error("Error fetching tables:", err);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenCartModal = (items = [], mode) => {
    setCartMode(mode);
    setSelectedRow([]);
    if (mode === "edit") {
      setSelectedRow(items);
    }
    setCartModalOpen((prev) => !prev);
  };

  const handleCloseCartModal = () => {
    setCartModalOpen((prev) => !prev);
    resetFilters();
  };

  const resetFilters = () => {
    const props = {
      fromDate: moment().subtract(8, "hours"),
      toDate: moment(),
      location: location[0]?.Uid || "",
      User,
    };
    setFilters((prev) => ({ ...prev, ...props }));
    setSelectedRow([]);
    setCartMode("add");
    setCartModalOpen(false);
    getOrderSummary(props); // will now send correct User value
  };

  const toggleStatusFilter = (status) => {
    setFilters((prev) => {
      const statusFilters = [...prev.statusFilters];
      const index = statusFilters.indexOf(status);
      if (index === -1) statusFilters.push(status);
      else statusFilters.splice(index, 1);
      return { ...prev, statusFilters };
    });
  };

  const filteredOrders = useMemo(() => {
    if (filters.statusFilters.length === 0) return orderSummary;
    return orderSummary.filter((order) =>
      filters.statusFilters.includes(order.A_Status)
    );
  }, [orderSummary, filters.statusFilters]);

  const getChipStyles = (status, isSelected) => {
    const styleMap = {
      Pending: {
        selected: { bg: "#FFF8E1", border: "#FFC107", color: "#FF9800" },
        unselected: { border: "#FFC107", color: "#FF9800" },
      },
      Completed: {
        selected: { bg: "#E8F5E9", border: "#4CAF50", color: "#2E7D32" },
        unselected: { border: "#4CAF50", color: "#2E7D32" },
      },
      Cancelled: {
        selected: { bg: "#FFEBEE", border: "#F44336", color: "#C62828" },
        unselected: { border: "#F44336", color: "#C62828" },
      },
    };

    const style = isSelected
      ? styleMap[status].selected
      : styleMap[status].unselected;

    return {
      backgroundColor: style.bg || "#fff",
      color: style.color,
      border: `2px solid ${style.border}`,
      fontWeight: 600,
      borderRadius: "20px",
      px: 1,
      "& .MuiChip-deleteIcon": {
        color: style.color,
        visibility: "visible", // Always show
      },
      "&:hover": {
        backgroundColor: style.bg || "#fafafa",
        boxShadow: `0 0 5px ${style.border}`,
      },
    };
  };

  const handleDeleteOrder = async (order) => {
    try {
      const res = await fetch(
        "http://localhost:4999/QuickSale/INV06TOA_INV06TOB_Delete",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            TransId: order.A_Transid,
            UserCd: User,
          }),
        }
      );
      const data = await res.json();
      if (data.Type === "Success") {
        fAlertToast(
          "SUCCESS",
          `Order ${order.A_Transid} deleted successfully!`
        );
        // Refresh the order summary after deletion
        getOrderSummary(filters);
      }
    } catch (err) {
      console.error("Error fetching tables:", err);
    }
  };
  const orderCounts = useMemo(() => {
    const statusMap = {
      ENTER: "Pending",
      POST: "Completed",
      DELETE: "Cancelled",
    };

    return orderSummary.reduce(
      (acc, order) => {
        const readableStatus = statusMap[order.A_Status] || "Other";
        if (readableStatus in acc) {
          acc[readableStatus]++;
        }
        return acc;
      },
      { Pending: 0, Completed: 0, Cancelled: 0 }
    );
  }, [orderSummary]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {cartModalOpen && (
        <CartModal
          open={cartModalOpen}
          handleClose={handleCloseCartModal}
          mode={cartMode}
          location={filters.location}
          User={User}
          selectedRow={selectedRow}
          table={filters.table}
        />
      )}
      <Grid container spacing={3} sx={{ p: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <DatePicker
                    label="From Date"
                    value={filters.fromDate}
                    onChange={(date) => {
                      if (moment.isMoment(date) && date.isValid()) {
                        handleFilterChange("fromDate", date);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                  />
                </Grid>
                <Grid item>
                  <DatePicker
                    label="To Date"
                    value={filters.toDate}
                    onChange={(date) => {
                      handleFilterChange("toDate", date);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                  />
                </Grid>
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Location</InputLabel>
                    <Select
                      label="Location"
                      value={filters.location}
                      onChange={(e) =>
                        handleFilterChange("location", e.target.value)
                      }
                    >
                      {location.map((loc) => (
                        <MenuItem key={loc.UId} value={loc.Uid}>
                          {loc.Name1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Table</InputLabel>
                    <Select
                      label="Table"
                      value={filters.table}
                      onChange={(e) =>
                        handleFilterChange("table", e.target.value)
                      }
                    >
                      {tableList.map((tbl) => (
                        <MenuItem key={tbl.Uid} value={tbl.Uid}>
                          {tbl.Name1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Search />}
                    onClick={() => getOrderSummary(filters)}
                  >
                    Search
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<RestartAlt />}
                    onClick={resetFilters}
                  >
                    Reset
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddCircle />}
                    onClick={() => handleOpenCartModal([], "add")}
                  >
                    Add Order
                  </Button>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} mt={2}>
                {["Pending", "Completed", "Cancelled"].map((status) => {
                  const statusMap = {
                    Pending: "ENTER",
                    Completed: "POST",
                    Cancelled: "Cancelled",
                  };

                  const internalStatus = statusMap[status];
                  const isSelected =
                    filters.statusFilters.includes(internalStatus);
                  const count = orderCounts[status] || 0;

                  // Badge only shown when not selected
                  const chipElement = (
                    <Chip
                      key={status}
                      label={status}
                      clickable={!isSelected}
                      onClick={
                        !isSelected
                          ? () => toggleStatusFilter(internalStatus)
                          : undefined
                      }
                      onDelete={
                        isSelected
                          ? () => toggleStatusFilter(internalStatus)
                          : undefined
                      }
                      sx={getChipStyles(status, isSelected)}
                      variant="outlined"
                    />
                  );

                  return !isSelected ? (
                    <Badge
                      key={status}
                      badgeContent={count}
                      color="primary"
                      overlap="circular"
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      sx={{
                        mr: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: 18,
                          minWidth: 18,
                          fontWeight: "bold",
                          borderRadius: "50%",
                          backgroundColor: "#1976d2", // primary color
                          color: "#fff",
                          boxShadow: "0 0 0 2px white",
                        },
                      }}
                    >
                      {chipElement}
                    </Badge>
                  ) : (
                    chipElement
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader
              title={
                <Typography variant="h6" color="primary">
                  Order Management
                </Typography>
              }
            />
            <CardContent>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: "50vh", overflowY: "auto" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      {[
                        "Order ID",
                        "Date",
                        "Table",
                        "Waiter",
                        "Status",
                        "Edit",
                        "Delete",
                      ].map((header) => (
                        <TableCell key={header} sx={{ fontWeight: 600 }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.length > 0 ? (
                      filteredOrders.map((order) => (
                        <TableRow key={order.A_Transid} hover>
                          <TableCell>{order.A_Transid}</TableCell>
                          <TableCell>
                            {moment(order.date).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell>{order.TableName}</TableCell>
                          <TableCell>{order.A_EnBy}</TableCell>
                          <TableCell>
                            <VerifiedIcon
                              sx={{
                                color:
                                  order.A_Status?.toUpperCase() === "ENTER"
                                    ? "#FFC107"
                                    : order.A_Status?.toUpperCase() === "POST"
                                    ? "#4CAF50"
                                    : "#F44336",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleOpenCartModal(order, "edit")}
                              disabled={
                                order.A_Status?.toUpperCase() === "POST"
                              }
                            >
                              <Edit
                                fontSize="small"
                                disabled={
                                  order.A_Status?.toUpperCase() === "POST" ||
                                  order.A_Status?.toUpperCase() === "DELETE"
                                }
                                color={
                                  order.A_Status?.toUpperCase() === "POST" ||
                                  order.A_Status?.toUpperCase() === "DELETE"
                                    ? "disabled"
                                    : "primary"
                                }
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteOrder(order)}
                              disabled={
                                order.A_Status?.toUpperCase() === "POST"
                              }
                            >
                              <DeleteOutline
                                fontSize="small"
                                disabled={
                                  order.A_Status?.toUpperCase() === "POST" ||
                                  order.A_Status?.toUpperCase() === "DELETE"
                                }
                                color={
                                  order.A_Status?.toUpperCase() === "POST" ||
                                  order.A_Status?.toUpperCase() === "DELETE"
                                    ? "disabled"
                                    : "primary"
                                }
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No orders found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default RestaurantOrderSummary;
